import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GetRequest } from "../../../../api/Request";
import { BuyApprovalStatusEnum } from "../../../../enum";
import {
  BuyApplication,
  OnlineStore,
  OnlineStoreSummary,
} from "./onlineOrders.types";
import {
  buyStoreBaseUrl,
  formatAddress,
  formatCurrency,
  formatDateTime,
  formatNumber,
  getBuyState,
} from "./onlineStore.util";
import { useBuyApproval } from "./useBuyApproval";
import "../styles/onlineOrdersDashboard.scss";

const storePageLimit = 20;
const applicationPageLimit = 12;
const chartDayCount = 31;

type ApplicationTab =
  | BuyApprovalStatusEnum.Pending
  | BuyApprovalStatusEnum.Rejected;
type ChartMetric = "orderCount" | "revenue";

const toIstDateKey = (date: Date) =>
  date.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const StoreLink: React.FC<{ slug?: string }> = ({ slug }) =>
  slug ? (
    <a
      className="store-link"
      href={`${buyStoreBaseUrl}${slug}`}
      target="_blank"
      rel="noreferrer"
    >
      modulusbuy.com/store/{slug}
    </a>
  ) : null;

const StoreLogo: React.FC<{ src?: string; name: string; size?: "sm" | "lg" }> = ({
  src,
  name,
  size = "sm",
}) =>
  src ? (
    <img className={`store-logo store-logo--${size}`} src={src} alt={name} />
  ) : (
    <div className={`store-logo store-logo--${size} store-logo--empty`}>
      {name?.charAt(0)?.toUpperCase() || "?"}
    </div>
  );

const OnlineOrdersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<OnlineStoreSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartMetric, setChartMetric] = useState<ChartMetric>("orderCount");

  const [applicationTab, setApplicationTab] = useState<ApplicationTab>(
    BuyApprovalStatusEnum.Pending,
  );
  const [applicationList, setApplicationList] = useState<BuyApplication[]>([]);
  const [applicationPage, setApplicationPage] = useState(1);
  const [applicationLoading, setApplicationLoading] = useState(false);

  const [storeList, setStoreList] = useState<OnlineStore[]>([]);
  const [storePage, setStorePage] = useState(1);
  const [storeLoading, setStoreLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchName, setSearchName] = useState("");

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setError(null);
      const response = await GetRequest("admin/v1/store/online/summary");
      setSummary(response.data);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch online order summary");
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchApplicationList = useCallback(async () => {
    try {
      setApplicationLoading(true);
      const response = await GetRequest(
        "admin/v1/store/buy-application",
        `status=${applicationTab}&page=${applicationPage}&limit=${applicationPageLimit}`,
      );
      setApplicationList(response.data || []);
    } catch {
      setApplicationList([]);
    } finally {
      setApplicationLoading(false);
    }
  }, [applicationTab, applicationPage]);

  const fetchStoreList = useCallback(async () => {
    try {
      setStoreLoading(true);
      const nameParam = searchName
        ? `&name=${encodeURIComponent(searchName)}`
        : "";
      const response = await GetRequest(
        "admin/v1/store/online",
        `page=${storePage}&limit=${storePageLimit}${nameParam}`,
      );
      setStoreList(response.data || []);
    } catch {
      setStoreList([]);
    } finally {
      setStoreLoading(false);
    }
  }, [storePage, searchName]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchApplicationList();
  }, [fetchApplicationList]);

  useEffect(() => {
    fetchStoreList();
  }, [fetchStoreList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStorePage(1);
      setSearchName(searchText.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const chartPointList = useMemo(() => {
    const dayMap = new Map(
      (summary?.dailyList || []).map((day) => [day.date, day]),
    );
    return Array.from({ length: chartDayCount }, (_, index) => {
      const date = new Date(
        Date.now() - (chartDayCount - 1 - index) * 24 * 60 * 60 * 1000,
      );
      const key = toIstDateKey(date);
      const day = dayMap.get(key);
      return {
        label: date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          timeZone: "Asia/Kolkata",
        }),
        orderCount: day?.orderCount ?? 0,
        revenue: day?.revenue ?? 0,
      };
    });
  }, [summary]);

  const refreshAll = () => {
    fetchSummary();
    fetchApplicationList();
    fetchStoreList();
  };

  const { confirmApprove, openReject, isReviewSaving, rejectModal } =
    useBuyApproval(refreshAll);

  const changeApplicationTab = (tab: ApplicationTab) => {
    setApplicationPage(1);
    setApplicationTab(tab);
  };

  if (summaryLoading && !summary) {
    return (
      <div className="online-orders">
        <div className="online-orders__loading">Loading online orders...</div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="online-orders">
        <div className="online-orders__error">{error}</div>
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="online-orders">
      <div className="online-orders__header">
        <div>
          <h1>Online Orders</h1>
          <p>
            Stores selling on BUY, the business they get online, and new
            requests to go live
          </p>
        </div>
        <button
          onClick={refreshAll}
          disabled={summaryLoading || storeLoading || applicationLoading}
        >
          Refresh
        </button>
      </div>

      <div className="online-orders__stats">
        <div className="online-orders__stat-card online-orders__stat-card--primary">
          <div className="stat-label">Online Stores</div>
          <div className="stat-value">
            {formatNumber(summary.onlineStoreCount)}
          </div>
          <div className="stat-subtitle">
            {formatNumber(summary.liveStoreCount)} taking orders right now
          </div>
        </div>
        <div className="online-orders__stat-card online-orders__stat-card--warning">
          <div className="stat-label">Waiting for Approval</div>
          <div className="stat-value">
            {formatNumber(summary.pendingApprovalCount)}
          </div>
          <div className="stat-subtitle">New BUY requests to review</div>
        </div>
        <div className="online-orders__stat-card online-orders__stat-card--info">
          <div className="stat-label">Online Orders</div>
          <div className="stat-value">
            {formatNumber(summary.onlineOrderCount)}
          </div>
          <div className="stat-subtitle">
            {formatNumber(summary.recentOnlineOrderCount)} in last 30 days
          </div>
        </div>
        <div className="online-orders__stat-card online-orders__stat-card--success">
          <div className="stat-label">Online Business</div>
          <div className="stat-value">
            {formatCurrency(summary.onlineRevenue)}
          </div>
          <div className="stat-subtitle">
            {formatCurrency(summary.recentOnlineRevenue)} in last 30 days
          </div>
        </div>
        <div className="online-orders__stat-card online-orders__stat-card--muted">
          <div className="stat-label">Average Order Value</div>
          <div className="stat-value">
            {formatCurrency(summary.averageOrderValue)}
          </div>
          <div className="stat-subtitle">Per paid online order</div>
        </div>
      </div>

      <div className="online-orders__panel">
        <div className="panel-header">
          <h2>Last 30 days</h2>
          <div className="segmented">
            <button
              className={chartMetric === "orderCount" ? "active" : ""}
              onClick={() => setChartMetric("orderCount")}
            >
              Orders
            </button>
            <button
              className={chartMetric === "revenue" ? "active" : ""}
              onClick={() => setChartMetric("revenue")}
            >
              Business
            </button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={chartPointList}>
            <defs>
              <linearGradient id="onlineOrdersGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4a90e2" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#4a90e2" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis
              tick={{ fontSize: 11 }}
              allowDecimals={chartMetric === "revenue"}
              tickFormatter={(value: number) =>
                chartMetric === "revenue" ? `₹${formatNumber(value)}` : `${value}`
              }
            />
            <Tooltip
              formatter={(value: number) =>
                chartMetric === "revenue"
                  ? [formatCurrency(value), "Business"]
                  : [`${value} orders`, "Orders"]
              }
            />
            <Area
              type="monotone"
              dataKey={chartMetric}
              stroke="#4a90e2"
              strokeWidth={2}
              fill="url(#onlineOrdersGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="online-orders__panel">
        <div className="panel-header">
          <h2>Approval requests</h2>
          <div className="segmented">
            <button
              className={
                applicationTab === BuyApprovalStatusEnum.Pending ? "active" : ""
              }
              onClick={() => changeApplicationTab(BuyApprovalStatusEnum.Pending)}
            >
              Pending ({formatNumber(summary.pendingApprovalCount)})
            </button>
            <button
              className={
                applicationTab === BuyApprovalStatusEnum.Rejected ? "active" : ""
              }
              onClick={() => changeApplicationTab(BuyApprovalStatusEnum.Rejected)}
            >
              Rejected
            </button>
          </div>
        </div>

        {applicationLoading && applicationList.length === 0 ? (
          <div className="no-data">Loading requests...</div>
        ) : applicationList.length === 0 ? (
          <div className="no-data">
            {applicationTab === BuyApprovalStatusEnum.Pending
              ? "No stores are waiting for approval."
              : "No rejected stores."}
          </div>
        ) : (
          <div className="application-grid">
            {applicationList.map((application) => {
              const bannerList = application.imageList || [];
              const isPending =
                application.buyConfig?.approvalStatus ===
                BuyApprovalStatusEnum.Pending;
              return (
                <div key={application._id} className="application-card">
                  <div className="application-card__banner">
                    {bannerList[0] ? (
                      <img src={bannerList[0]} alt={`${application.name} banner`} />
                    ) : (
                      <span>No banner</span>
                    )}
                  </div>

                  <div className="application-card__body">
                    <div className="application-card__title">
                      <StoreLogo
                        src={application.profileImage}
                        name={application.name}
                        size="lg"
                      />
                      <div>
                        <h3>{application.name}</h3>
                        <StoreLink slug={application.slug} />
                      </div>
                    </div>

                    <dl className="application-card__details">
                      <dt>Helpline</dt>
                      <dd>{application.phoneNumberList?.[0]?.phoneNumber || "-"}</dd>
                      <dt>Email</dt>
                      <dd>{application.email || "-"}</dd>
                      <dt>Address</dt>
                      <dd>{formatAddress(application.address) || "-"}</dd>
                      <dt>Store type</dt>
                      <dd>{application.typeList?.join(", ") || "-"}</dd>
                      <dt>Category</dt>
                      <dd>{application.categoryList?.join(", ") || "-"}</dd>
                      <dt>Applied on</dt>
                      <dd>{formatDateTime(application.buyConfig?.appliedAt)}</dd>
                      {!isPending && (
                        <>
                          <dt>Rejected on</dt>
                          <dd>{formatDateTime(application.buyConfig?.reviewedAt)}</dd>
                        </>
                      )}
                    </dl>

                    {bannerList.length > 1 && (
                      <div className="application-card__thumbs">
                        {bannerList.map((image, index) => (
                          <a key={image} href={image} target="_blank" rel="noreferrer">
                            <img src={image} alt={`Banner ${index + 1}`} />
                          </a>
                        ))}
                      </div>
                    )}

                    {!isPending && application.buyConfig?.rejectionReason && (
                      <div className="application-card__reason">
                        <b>Reason:</b> {application.buyConfig.rejectionReason}
                      </div>
                    )}

                    <div className="application-card__actions">
                      <button
                        className="btn btn--approve"
                        disabled={isReviewSaving}
                        onClick={() =>
                          confirmApprove(application._id, application.name)
                        }
                      >
                        Approve
                      </button>
                      {isPending && (
                        <button
                          className="btn btn--reject"
                          disabled={isReviewSaving}
                          onClick={() =>
                            openReject({
                              _id: application._id,
                              name: application.name,
                              isLive: false,
                            })
                          }
                        >
                          Reject
                        </button>
                      )}
                      <button
                        className="btn btn--ghost"
                        onClick={() => navigate(`/store/${application._id}/analytics`)}
                      >
                        Store details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(applicationPage > 1 ||
          applicationList.length === applicationPageLimit) && (
          <div className="online-orders__pagination">
            <button
              onClick={() => setApplicationPage((p) => Math.max(1, p - 1))}
              disabled={applicationPage === 1 || applicationLoading}
            >
              Previous
            </button>
            <span>Page {applicationPage}</span>
            <button
              onClick={() => setApplicationPage((p) => p + 1)}
              disabled={
                applicationList.length < applicationPageLimit ||
                applicationLoading
              }
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="online-orders__panel">
        <div className="panel-header">
          <h2>Online stores</h2>
          <input
            className="search-input"
            type="text"
            placeholder="Search store name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {storeLoading && storeList.length === 0 ? (
          <div className="no-data">Loading stores...</div>
        ) : storeList.length === 0 ? (
          <div className="no-data">
            {searchName
              ? "No online store matches this name."
              : storePage > 1
                ? "No more stores."
                : "No online stores yet."}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="online-orders__table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Status</th>
                  <th>Helpline</th>
                  <th className="num">Orders</th>
                  <th className="num">Business</th>
                  <th className="num">Avg. order</th>
                  <th className="num">Delivered</th>
                  <th className="num">Cancelled</th>
                  <th>Last order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {storeList.map((store) => {
                  const state = getBuyState(store.buyConfig);
                  const approvalStatus = store.buyConfig?.approvalStatus;
                  return (
                    <tr key={store._id}>
                      <td>
                        <div className="store-cell">
                          <StoreLogo src={store.profileImage} name={store.name} />
                          <div>
                            <div className="store-name">{store.name}</div>
                            <StoreLink slug={store.slug} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`state-badge state-badge--${state.className}`}>
                          {state.label}
                        </span>
                      </td>
                      <td>{store.phoneNumberList?.[0]?.phoneNumber || "-"}</td>
                      <td className="num">
                        <b>{formatNumber(store.onlineStats.onlineOrderCount)}</b>
                        <div className="sub-text">
                          {formatNumber(store.onlineStats.recentOnlineOrderCount)} in 30 days
                        </div>
                      </td>
                      <td className="num">
                        <b>{formatCurrency(store.onlineStats.onlineRevenue)}</b>
                        <div className="sub-text">
                          {formatCurrency(store.onlineStats.recentOnlineRevenue)} in 30 days
                        </div>
                      </td>
                      <td className="num">
                        {formatCurrency(store.onlineStats.averageOrderValue)}
                      </td>
                      <td className="num">
                        {formatNumber(store.onlineStats.deliveredOrderCount)}
                      </td>
                      <td className="num">
                        {formatNumber(store.onlineStats.cancelledOrderCount)}
                      </td>
                      <td>{formatDateTime(store.onlineStats.lastOnlineOrderAt)}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="btn btn--ghost"
                            onClick={() => navigate(`/store/${store._id}/analytics`)}
                          >
                            View
                          </button>
                          {approvalStatus === BuyApprovalStatusEnum.Pending && (
                            <>
                              <button
                                className="btn btn--approve"
                                disabled={isReviewSaving}
                                onClick={() => confirmApprove(store._id, store.name)}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn--reject"
                                disabled={isReviewSaving}
                                onClick={() =>
                                  openReject({
                                    _id: store._id,
                                    name: store.name,
                                    isLive: false,
                                  })
                                }
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {approvalStatus === BuyApprovalStatusEnum.Approved && (
                            <button
                              className="btn btn--reject"
                              disabled={isReviewSaving}
                              onClick={() =>
                                openReject({
                                  _id: store._id,
                                  name: store.name,
                                  isLive: true,
                                })
                              }
                            >
                              Take off BUY
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="online-orders__pagination">
          <button
            onClick={() => setStorePage((p) => Math.max(1, p - 1))}
            disabled={storePage === 1 || storeLoading}
          >
            Previous
          </button>
          <span>Page {storePage}</span>
          <button
            onClick={() => setStorePage((p) => p + 1)}
            disabled={storeList.length < storePageLimit || storeLoading}
          >
            Next
          </button>
        </div>
      </div>

      {rejectModal}
    </div>
  );
};

export default OnlineOrdersDashboard;
